/**
 *	Runtime lookup and loading for documentation pages.
 *
 *	The manifest is generated per app (see @shared/bun/docs); everything here is
 *	the read side — turning a `page` reference into a page, and a page into a
 *	renderable Markdoc tree. One runtime per manifest, so two apps mounted in
 *	the same process never share a cache.
 */
import Markdoc, {
	type Config,
	type RenderableTreeNode,
} from '@markdoc/markdoc';

import { withHeadingIds } from '@shared/docs/markdoc';
import {
	DOCS_SOURCE_ROUTE,
	type DocumentManifest,
	type DocumentPage,
} from '@shared/docs/types';

export interface DocumentRuntime {
	resolve(page: string): Optional<DocumentPage>;
	load(page: DocumentPage, config: Config): Promise<RenderableTreeNode>;
	invalidate(): void;
	versions(): string[];
	forVersion(version: string): DocumentPage[];
}

/**
 *	Keyed by the manifest object rather than by app name. Apps pass their
 *	manifest *module namespace*, whose identity survives a hot update while its
 *	bindings do not — which is what lets the indexes below notice a changed page
 *	set without the whole tree re-mounting.
 */
const runtimes: WeakMap<DocumentManifest, DocumentRuntime> = new WeakMap();

/** Strips the `~/` docs-root prefix and any `./`, leaving a manifest source. */
function toSourceKey(page: string): string {
	return page.replace(/^~\//, '').replace(/^\.\//, '');
}

function createRuntime(manifest: DocumentManifest): DocumentRuntime {
	/**
	 *	Rebuilt whenever the `pages` binding itself changes rather than built
	 *	once. The manifest module is self-accepting, so a markdown edit
	 *	re-evaluates *it* and swaps its exports in place. An index captured once
	 *	would go on serving page objects whose `load()` closes over the previous
	 *	chunk.
	 */
	let indexedPages: Optional<DocumentPage[]>;
	let byUrl: Map<string, DocumentPage> = new Map();
	let bySource: Map<string, DocumentPage> = new Map();

	function index(): void {
		if (indexedPages === manifest.pages) {
			return;
		}

		byUrl = new Map(manifest.pages.map((page) => [page.url, page]));
		bySource = new Map(manifest.pages.map((page) => [page.source, page]));
		indexedPages = manifest.pages;
	}

	/**
	 *	Cache of in-flight/resolved transforms, keyed by URL.
	 *
	 *	`use()` requires the *same* promise across renders — resolving a fresh
	 *	one each time would suspend forever. This map is what makes the page
	 *	stable.
	 */
	const transformed: Map<string, Promise<RenderableTreeNode>> = new Map();

	/**
	 *	Bumped by every invalidation, and used to bust the dev fetch below.
	 *	Without it a re-read could be answered from the HTTP cache.
	 */
	let revision = 0;

	/**
	 *	Reads a page's markdown source.
	 *
	 *	Production goes through the manifest's dynamic `import()`, which is what
	 *	gets the doc code-split into its own chunk.
	 *
	 *	Development cannot: Bun rebuilds the chunk on edit, but the browser's
	 *	module registry has already evaluated it, so re-invoking the import
	 *	returns the text from *before* the edit — the cache clear and re-render
	 *	still paint the old page. Reading over the dev-only source route
	 *	bypasses the registry. Eliminated from production builds along with the
	 *	`import.meta.hot` branch.
	 */
	function loadSource(page: DocumentPage): Promise<string> {
		if (import.meta.hot) {
			const url = `${DOCS_SOURCE_ROUTE}/${page.source}?r=${revision}`;

			return fetch(url, { cache: 'no-store' }).then((response) => {
				if (!response.ok) {
					throw new Error(`Unable to read ${page.source} (${response.status})`);
				}

				return response.text();
			});
		}

		return page.load();
	}

	return {
		/**
		 *	Accepts either spelling: a canonical URL (`/docs/getting-started`),
		 *	following a version alias when only the versioned page exists, or a
		 *	manifest source path (`learn/README.md`, `~/learn/README.md`,
		 *	`design/atoms/button/README.md`).
		 *
		 *	A URL is absolute and a source never is, which is what tells the two
		 *	apart — an app can serve several collections, so there is no single
		 *	root prefix to test against.
		 */
		resolve(page: string): Optional<DocumentPage> {
			index();

			if (page.startsWith('/')) {
				const direct = byUrl.get(page);

				if (direct) {
					return direct;
				}

				const aliased = manifest.aliases[page];

				return aliased ? byUrl.get(aliased) : undefined;
			}

			return bySource.get(toSourceKey(page));
		},

		load(page: DocumentPage, config: Config): Promise<RenderableTreeNode> {
			const cached = transformed.get(page.url);

			if (cached) {
				return cached;
			}

			// The page's own source path goes in as a variable so a tag can
			// resolve something relative to the page it sits on — the playbook's
			// `{% preview %}` finds its entity that way, see
			// playbook/www/config/markdoc.tsx. Its frontmatter rides alongside,
			// already parsed by the generator, so a node transform can read the
			// page's metadata without the browser owning a YAML parser.
			//
			// Heading ids are given out afterwards rather than by a node
			// transform, because they have to be unique across the whole page
			// and because an app may have replaced the `heading` node — see
			// withHeadingIds.
			const pending = loadSource(page).then((source) =>
				withHeadingIds(
					Markdoc.transform(Markdoc.parse(source), {
						...config,
						variables: {
							...config.variables,
							page: page.source,
							frontmatter: page.frontmatter,
						},
					})
				)
			);

			transformed.set(page.url, pending);

			// A failed load must not poison the cache — otherwise a transient
			// chunk fetch error would make the page permanently unrenderable
			// for the session.
			pending.catch(() => transformed.delete(page.url));

			return pending;
		},

		/**
		 *	Drops every memoized transform so the next render re-reads its
		 *	source. Needed for hot reloading: a doc's `.md` sits behind the
		 *	dynamic `import()` in the generated manifest, which is its own hot
		 *	update boundary. Bun swaps the chunk but never re-evaluates this
		 *	module, so the cache above would keep handing back the promise
		 *	resolved from the *previous* source. See useDocumentHotReload.
		 */
		invalidate(): void {
			transformed.clear();
			revision += 1;
		},

		/** Version modifiers present in the docs, highest first. */
		versions(): string[] {
			const versions = new Set(
				manifest.pages
					.map((page) => page.version)
					.filter(
						(version): version is NonNullable<typeof version> => !!version
					)
			);

			return [...versions].sort(
				(a, b) => Number(b.slice(1)) - Number(a.slice(1))
			);
		},

		/** Every page that carries the given version modifier. */
		forVersion(version: string): DocumentPage[] {
			return manifest.pages.filter((page) => page.version === version);
		},
	};
}

export function documentRuntime(manifest: DocumentManifest): DocumentRuntime {
	const existing = runtimes.get(manifest);

	if (existing) {
		return existing;
	}

	const runtime = createRuntime(manifest);

	runtimes.set(manifest, runtime);

	return runtime;
}
